import {
  ChangeEvent,
  FormEvent,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Field from './Field';
import { InputPrimitive } from './Input';

export type CheckboxPrimitiveProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'id'
> & {
  /**
   * ID is required for accessibility as it is used to associate the checkbox input with its label and error messages.
   */
  id: string;
  /**
   * Validation messages allow for custom error messages to be displayed based on the validity state of the checkbox input.
   */
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
};

/**
 * CheckBoxPrimitive is a low-level component that renders an input element of type checkbox with additional functionality for handling validation and user interaction states.
 * It is used by the higher-level Checkbox component, but can also be used directly if you need more control over the checkbox input element.
 *
 * Note: Considering using the BaseUI Checkbox as it includes a more complete implementation for the different states and data attributes.
 */
export const CheckboxPrimitive = forwardRef<
  HTMLInputElement,
  CheckboxPrimitiveProps
>(({ id, ...checkboxProps }, ref) => (
  <InputPrimitive
    // oxlint-disable-next-line react/jsx-props-no-spreading -- the props extend the checkbox primitive props
    {...checkboxProps}
    type="checkbox"
    className={twMerge(
      'size-5 appearance-none rounded border-2 border-on-surface-variant ring-0 ring-offset-0 data-invalid:border-critical',
      'checked:border-0 checked:bg-brand checked:text-on-brand checked:data-invalid:bg-critical checked:data-invalid:text-on-critical',
      'disabled:pointer-events-none disabled:border-on-surface/(--state-disabled-mix) disabled:text-surface disabled:checked:bg-on-surface/(--state-disabled-mix)',
      'hover:scale-105 hover:border-on-surface hover:not-checked:bg-on-surface/(--state-hovered-mix) hover:checked:bg-mix hover:checked:mix-with-brand/(--state-hovered-mix) hover:checked:mix-brand hover:data-invalid:bg-critical/(--state-hovered-mix)',
      'focus-visible:border-on-surface focus-visible:outline-(length:--state-focused-outline-width) focus-visible:outline-offset-(--state-focused-outline-offset) focus-visible:outline-(color:--state-focused-outline-color)',
      'focus-visible:bg-on-surface/(--state-focused-mix) focus-visible:mix-with-brand/(--state-focused-mix) focus-visible:mix-brand focus-visible:checked:bg-mix focus-visible:data-invalid:bg-critical/(--state-focused-mix)',
      'active:scale-95 active:bg-on-surface/(--state-active-mix) active:mix-with-brand/(--state-active-mix) active:mix-brand active:checked:bg-mix active:data-invalid:bg-critical/(--state-active-mix)',
    )}
    id={id}
    ref={ref}
    aria-describedby={`${id}-help-text ${id}-error-text`}
  />
));

type ValidationConstraint = Exclude<keyof ValidityState, 'valid'>;

/**
 * Checkbox is a high-level component that renders a checkbox input with a label, description, and error messages.
 * It uses the CheckboxPrimitive component internally for the actual input element.
 */
export default function Checkbox({
  id,
  label,
  className,
  description,
  ...checkboxProps
}: {
  /**
   * The label is used to describe the purpose of the checkbox input and is associated with the input for accessibility.
   */
  label: string;
  /**
   * The description provides additional information about the checkbox input and is displayed below the label. It is also associated with the input for accessibility.
   */
  description?: ReactNode;
  /**
   * Validation messages allow for custom error messages to be displayed based on the validity state of the checkbox input.
   */
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
} & CheckboxPrimitiveProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleInvalid = (event: FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    setErrorMessage(input.validationMessage);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (input.validity.valid) {
      setErrorMessage(null);
    }

    checkboxProps.onChange?.(event);
  };

  return (
    <Field.Root
      className={twMerge('justify flex flex-row items-start gap-3', className)}
    >
      <CheckboxPrimitive
        // oxlint-disable-next-line react/jsx-props-no-spreading -- the props extend the checkbox primitive props
        {...checkboxProps}
        id={id}
        ref={ref}
        aria-describedby={`${id}-help-text ${id}-error-text`}
        onInvalid={handleInvalid}
        onChange={handleChange}
      />
      <div className="flex flex-col gap-1">
        <Field.Label className="cursor-pointer" htmlFor={id}>
          {`${label}${checkboxProps.required ? '' : ' (optional)'}`}
        </Field.Label>
        {errorMessage && (
          <Field.ErrorText id={`${id}-error-text`}>
            {errorMessage}
          </Field.ErrorText>
        )}
        {description && !errorMessage && (
          <Field.HelpText id={`${id}-help-text`}>{description}</Field.HelpText>
        )}
      </div>
    </Field.Root>
  );
}
